import { useCallback } from 'react';
import { createUuid } from '~/_shared/utils/createUuid';
import {
  type BoundaryTerritoryGroupDetailsFilterRequest, getBoundaryTerritories,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.repository';
import { requestTerritoriesRefresh } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { BOUNDARY_SETTINGS_MIN_RANGES_COUNT } from './boundarySettings.common';
import { getBoundaryTerritoryRegularGroupsCount } from './boundarySettings.helpers';

export type FetchBoundaryTerritoriesArguments = {
  newBoundaryTerritoryGroup: BoundaryTerritoryGroup;
  originalBoundaryTerritoryGroup: BoundaryTerritoryGroup;
  filter?: BoundaryTerritoryGroupDetailsFilterRequest | null;
};

export const useFetchBoundaryTerritories = (
  onBoundaryTerritoryGroupUpdate: (territoryGroup: BoundaryTerritoryGroup) => void,
  onIsLoadingChange: (isLoading: boolean) => void,
) => {
  const clientId = useClientIdSelector();

  return useCallback(({
    newBoundaryTerritoryGroup, originalBoundaryTerritoryGroup, filter,
  }: FetchBoundaryTerritoriesArguments) => {
    if (!clientId) {
      return;
    }

    const queryId = createUuid();
    const boundaryTerritoryGroupForRequest: BoundaryTerritoryGroup = {
      ...newBoundaryTerritoryGroup,
      settings: {
        ...newBoundaryTerritoryGroup.settings,
        boundaryTerritories: requestTerritoriesRefresh(
          newBoundaryTerritoryGroup.settings.boundaryTerritories,
          originalBoundaryTerritoryGroup.settings.boundaryTerritories
        ),
      },
    };

    onIsLoadingChange(true);
    getBoundaryTerritories(clientId, [{
      boundaryTerritoryGroup: boundaryTerritoryGroupForRequest,
      boundaryTerritoryGroupQueryId: queryId,
      filter: filter ?? null,
    }])
      .then(response => {
        const newTerritoryDetails = response.get(queryId);
        if (!newTerritoryDetails) {
          return;
        }

        onBoundaryTerritoryGroupUpdate({
          ...boundaryTerritoryGroupForRequest,
          settings: {
            ...boundaryTerritoryGroupForRequest.settings,
            // ensure the ranges count to be at least 2 to have a successful API call
            rangesCount: Math.max(getBoundaryTerritoryRegularGroupsCount(newTerritoryDetails.boundaryTerritories),
              BOUNDARY_SETTINGS_MIN_RANGES_COUNT),
            boundaryTerritories: newTerritoryDetails.boundaryTerritories,
          },
        });
      })
      .finally(() => {
        onIsLoadingChange(false);
      });
  }, [clientId, onBoundaryTerritoryGroupUpdate, onIsLoadingChange]);
};
